import dayjs from 'dayjs'
import { NextPage } from 'next'
import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StoreState } from 'state/ducks'

import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { getLocationCoinLockers } from 'state/ducks/locations/selectors'

//lib
import getAcceptLanguageByLocale from 'lib/getAcceptLanguageByLocale'
import getInfoByLocale from 'lib/getInfoByLocale'
import Meta from 'lib/meta'
import { addLocalePrefix } from 'lib/addLocalePrefix'
import { useAffiliateTracking } from 'hooks/tracking/useAffiliateTracking'
import useScrollPositionRestoration from 'hooks/common/useScrollPositionRestoration'
import {
  sendGtmEvent,
  GTM_EVENT_CLICK_FILTER_TODAY,
  GTM_EVENT_CLICK_FILTER_TOMORROW,
  GTM_EVENT_CLICK_FILTER_OPEN_24_HOURS,
} from 'lib/gtag'

//type
import { Prefecture } from 'state/ducks/prefecture/models'

// configs
import { COUNTRY_CODES } from 'configs/country'
import { PRICE_SIZE_TYPE_BAG, PRICE_SIZE_TYPE_SUITCASE } from 'configs/space'

//api
import { fetchPrefectureByIdOrSlug } from 'features/prefecture/services/prefectureDetailApi'
import { mapPrefectureItemToLocationDetail } from 'features/prefecture/lib/mapPrefectureItemToLocationDetail'
import { fetchFacilityById } from 'features/facility/services/facilityDetailApi'
import { mapFacilityToLocationDetail } from 'features/facility/lib/mapFacilityToLocationDetail'
import { buildNearbySpacesParams } from 'features/space/lib/buildNearbySpacesParams'
import { mapNearbySpaceToRecommendedSpace } from 'features/space/lib/mapNearbySpaceToRecommendedSpace'
import { useNearbySpaces } from 'features/space/hooks/useNearbySpaces'

import {
  fetchLocationCoinLockersRequest,
  fetchLocationDetailRequest,
} from 'state/ducks/locations/actions'

import { usePrefectures } from 'features/prefecture/hooks/usePrefectures'
import { getPrefectureCountryId } from 'features/prefecture/constants'
import { findPrefectureIdBySlug } from 'features/prefecture/lib/findPrefectureIdBySlug'

// hooks
import { useAreaLockers } from 'hooks/spaces/useAreaLockers'
import { useStations } from 'features/facility/hooks/useStations'
import { useFacilities } from 'features/facility/hooks/useFacilities'
import { useLegacyCoinLockers } from 'features/coin-locker/hooks/useLegacyCoinLockers'

//component
import LoadingComponent from 'components/loadings/LoadingComponent'
import PlanCard from 'components/cards/PlanCard'
import Layout from 'components/commons/layouts/Layout'
import LocationSection from 'components/domains/top/LocationSection'
import OwnerSection from 'components/domains/top/OwnerSection'

import BreadcrumbLayout from 'components/commons/layouts/BreadcrumbLayout'
import { Breadcrumb } from 'types/breadcrumb'

import ShareButtonsComponent from 'components/buttons/share/ShareButtonsComponent'
import FacilityRecommendationsSection from 'components/domains/facility/FacilityRecommendationsSection'
import RecommendedCoinLockerCardListComponent from 'components/cards/RecommendedCoinLockerCardList'
import RecommendedSpaceCardListComponent from 'components/cards/RecommendedSpaceCardList'
import CoinLockerMapComponent from 'components/domains/country/city/slug/CoinLockerMapSection'
import SearchForm from 'components/domains/country/city/slug/SearchForm'
import Qa from 'components/domains/spaces/Qa'
import CardStoreComponent from 'components/stores/CardStore'
import SectionBottomBorderTitleComponent from 'components/titles/SectionBottomBorderTitle'
import AccordionBlock from 'components/domains/country/city/slug/AccordionBlock'

//style
import { useCustomIntercom } from 'hooks/common/intercom'
import styles from './Slug.module.scss'

const SELECTED_TAB_KEY = {
  space: 'space',
  coinLocker: 'coinLocker',
} as const
type SELECTED_TAB_KEY_TYPE = keyof typeof SELECTED_TAB_KEY
type RecommendedFilterCondition = 'today' | 'tomorrow' | 'open24Hours'

const ITEM_COUNT = 20
const DEFAULT_SHARE_IMAGE_URL = `${process.env.DOMAIN}/location_popular_area_default.webp`

// https://ecbo.slack.com/archives/CD7P2G9KL/p1763552066856849?thread_ts=1762323946.583289&cid=CD7P2G9KL
const EXCEPTION_LOCATION_IDS = [84, 77] // 84: よみうりランド, 77: 東京ドーム

const CitySlugPage: NextPage = (props: any): JSX.Element => {
  useAffiliateTracking()
  const router = useRouter()
  const { asPath, locale: localePath, query } = router

  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Loading
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // 施設情報 (駅 or 施設)
  const { stations, fetchStations } = useStations()
  const { groupedFacilities, fetchFacilities } = useFacilities()
  const [thisPrefecture, setThisPrefecture] = useState<Prefecture>(null)

  //パンクズリスト
  const [breadcrumbMenus, setBreadcrumbMenus] = useState<Breadcrumb[]>([])

  //メインビジュアル
  const [mainVisualSectionStyle, setMainVisualSectionStyle] = useState({
    backgroundImage: null,
  })

  // タブ切り替え
  const [spaceCoinLockerTabKey, setSpaceCoinLockerTabKey] = useState<SELECTED_TAB_KEY_TYPE>(
    SELECTED_TAB_KEY.space
  )

  //おすすめ預かり場所
  const [recommendedFilter, setRecommendedFilter] = useState<RecommendedFilterCondition | null>(
    null
  )
  const isResettingRecommendedFilterPageRef = useRef(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const { lockers, getAreaLockers } = useAreaLockers()
  const { legacyCoinLockers, fetchLegacyCoinLockers } = useLegacyCoinLockers()

  // URLパラメータからページ番号を取得
  const currentPage = useMemo(() => {
    const pageParam = query.page
    const page = pageParam ? parseInt(pageParam as string, 10) : 1
    return page > 0 ? page : 1
  }, [query.page])

  const recommendedFilterOptions = useMemo(
    () => [
      {
        key: 'today' as const,
        label: t('AREAS.CITY.SLUG.RECOMMENDED_FILTER_TODAY_BUTTON'),
      },
      {
        key: 'tomorrow' as const,
        label: t('AREAS.CITY.SLUG.RECOMMENDED_FILTER_TOMORROW_BUTTON'),
      },
      {
        key: 'open24Hours' as const,
        label: t('AREAS.CITY.SLUG.RECOMMENDED_FILTER_OPEN_24_HOURS_BUTTON'),
      },
    ],
    [t]
  )

  const locationDetail = props.locationDetail
  const {
    spaces: nearbySpaces,
    totalCount: nearbyTotalCount,
    isLoading: isNearbySpacesLoading,
    loadNearbySpaces,
  } = useNearbySpaces()
  const shouldUseWithAreaTranslation = EXCEPTION_LOCATION_IDS.includes(locationDetail?.id)
  const recommendedSpacesTotalCount = nearbyTotalCount

  // Restore scroll on browser back/forward after recommended spaces have loaded.
  useScrollPositionRestoration(!!recommendedSpacesTotalCount)

  // コインロッカー
  const locationCoinLockers = useSelector((state: StoreState) => getLocationCoinLockers(state))

  // 今年の年度
  const thisYear: number = dayjs().year()

  /**
   * URIのパラメーターを整理する
   * @returns
   */
  const uriParams = useMemo(() => {
    // クエリパラメータを除外してパスのみを取得
    const pathOnly = asPath.split('?')[0]
    const path = pathOnly.split('/')

    const uriList = path.slice(1)

    if (uriList.length === 3) {
      //都道府県
      return {
        isCity: true,
        countryCode: uriList[0],
        citySlug: uriList.pop(),
      }
    } else {
      //駅 or 施設
      return {
        isCity: false,
        countryCode: uriList[0],
        citySlug: uriList[2],
        id: uriList.pop(),
      }
    }
  }, [asPath])
  const { prefectures } = usePrefectures({
    countryId: getPrefectureCountryId(uriParams.countryCode),
    enabled: uriParams.countryCode === 'jpn' || uriParams.countryCode === 'twn',
  })

  const facilityRecommendationConfig = useMemo(() => {
    if (!locationDetail) {
      return null
    }

    const isTaiwan = uriParams.countryCode === 'twn'
    const taiwanPrefectureId = isTaiwan
      ? findPrefectureIdBySlug(prefectures, uriParams.citySlug)
      : undefined

    if (uriParams.isCity) {
      const legacyId = locationDetail.id
      const prefectureId = isTaiwan ? taiwanPrefectureId ?? legacyId : legacyId
      if (prefectureId == null) return null

      return {
        type: 'city' as const,
        prefectureId,
      }
    }

    if (locationDetail.location_type?.name === 'station') {
      const legacyId = locationDetail.region.id
      const prefectureId = isTaiwan ? taiwanPrefectureId ?? legacyId : legacyId
      if (prefectureId == null) return null

      return {
        type: 'station' as const,
        prefectureId,
      }
    }

    return null
  }, [locationDetail, uriParams.isCity, uriParams.countryCode, uriParams.citySlug, prefectures])

  const metaTitle = useMemo(() => {
    const titleParameters = {
      year: thisYear,
      location: props.name,
    }
    if (currentPage > 1) {
      // ページ番号付きのタイトルを試し、存在しない場合はデフォルトにページ番号を追加
      return t(
        shouldUseWithAreaTranslation
          ? 'META.TITLE.CITY.SLUG_YEAR_LOCATION_PAGE_WITH_AREA'
          : 'META.TITLE.CITY.SLUG_YEAR_LOCATION_PAGE',
        {
          ...titleParameters,
          currentPage: currentPage,
          totalPage: Math.ceil(recommendedSpacesTotalCount / ITEM_COUNT),
        }
      )
    }
    return t(
      shouldUseWithAreaTranslation
        ? 'META.TITLE.CITY.SLUG_YEAR_LOCATION_WITH_AREA'
        : 'META.TITLE.CITY.SLUG_YEAR_LOCATION',
      titleParameters
    )
  }, [
    t,
    thisYear,
    props.name,
    currentPage,
    recommendedSpacesTotalCount,
    shouldUseWithAreaTranslation,
  ])

  const metaDescription = useMemo(() => {
    if (currentPage > 1) {
      // ページ番号付きのdescriptionを試し、存在しない場合はデフォルトにページ番号を追加
      const pageDescription = t('META.DESCRIPTION.CITY.SLUG_PAGE', {
        location: props.name,
        page: currentPage,
        currentPage: currentPage,
        totalPage: Math.ceil(recommendedSpacesTotalCount / ITEM_COUNT),
      })

      return pageDescription
    }
    return t('META.DESCRIPTION.CITY.SLUG', { location: props.name })
  }, [t, props.name, currentPage, recommendedSpacesTotalCount])

  /**
   * エリア詳細を取得する
   * @returns
   */
  const getLocationData = () => {
    if (uriParams.isCity) {
      return getInfoByLocale(props.locale, {
        ja: {
          prefecture_name: locationDetail.name,
          location_name: locationDetail.name,
        },
        en: {
          prefecture_name: locationDetail.en_name,
          location_name: locationDetail.en_name,
        },
      })
    } else {
      return getInfoByLocale(props.locale, {
        ja: {
          prefecture_name: locationDetail.region?.name,
          location_name: locationDetail.name,
        },
        en: {
          prefecture_name: locationDetail.region?.en_name,
          location_name: locationDetail.en_name,
        },
      })
    }
  }

  /**
   * 初期化処理
   * パンクズリスト
   */
  const initBreadcrumbMenus = () => {
    const menus = getGetBreadcrumbMenus()
    setBreadcrumbMenus(menus)
  }

  /**
   * パンクズリストを取得する
   * @returns
   */
  const getGetBreadcrumbMenus = () => {
    const menus = []

    let basePath = `/${props.locale}/${uriParams.countryCode}/city`
    basePath = basePath.replace('zh_tw', 'zh-TW')

    //city
    const menu1: Breadcrumb = {
      label: uriParams.isCity
        ? t('AREAS.CITY.SLUG.BREADCRUMB', { location: getLocationData().prefecture_name })
        : getLocationData().prefecture_name,
      path: `${basePath}/${uriParams.citySlug}`,
    }
    menus.push(menu1)

    if (!uriParams.isCity) {
      //駅 or 施設
      const menu2: Breadcrumb = {
        label: t('AREAS.CITY.SLUG.BREADCRUMB', { location: props.name }),
      }
      menus.push(menu2)
    }
    return menus
  }

  const { openIntercomMessage } = useCustomIntercom()

  /**
   * シェア文言を取得する
   * @returns string
   */
  const getShareText = () => {
    let text: string = ''
    switch (localePath) {
      case 'ja':
        text = `${props.name}でおすすめ荷物預かり場所とコインロッカー情報 - 荷物預けるなら ecbo cloak（エクボクローク）`
        break
      case 'en':
        text = `Recommended Luggage Storage Locations and Coin Locker Information at ${props.name} - If you want to leave your luggage, ecbo cloak`
        break
      case 'zh-TW':
        text = `${props.name}的推薦行李寄存地點和投幣式儲物櫃信息 - 如果你想寄存行李, ecbo cloak`
        break
    }
    return text
  }

  /**
   * 最終更新日のテキストを取得
   * @returns
   */
  const getLastUpdateText = () => {
    let text: string = ''
    // let date: string = dayjs(locationDetail.updated_at).format('YYYY/M/d')
    let date: string = dayjs().format('YYYY/M/D')

    // if (!locationDetail.updated_at) {
    //   return text
    // }

    switch (localePath) {
      case 'ja':
        // date = dayjs(locationDetail.updated_at).format('YYYY年M月d日')
        date = dayjs().format('YYYY年M月D日')
        text = `最終更新日：${date}`
        break
      case 'en':
        text = `Last update：${date}`
        break
      case 'zh-TW':
        text = `最近更新時間：${date}`
        break
    }
    return text
  }

  /**
   * コインロッカーMapのピンをクリックしたら、該当セクションまでスクロール処理
   * @param {number} id コインロッカーID
   * @param {boolean} isOld 旧コインロッカーかどうか
   */
  const scrollToRecommendedCoinLockerSection = (id: number, isOld: boolean = false) => {
    // Create ID based on whether it's an old coin locker or regular one
    const sectionId: string = isOld
      ? `recommended-coin-locker-card-old-${id}`
      : `recommended-coin-locker-card-${id}`

    const element = document.getElementById(sectionId)

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }

  /**
   * ShareするURLを取得する
   * @returns
   */
  const createShareUrl = (): string => {
    let url: string = `${process.env.DOMAIN}/${localePath}${asPath}`

    // ハッシュタグがある場合は取り除く
    if (url.indexOf('#')) {
      url = url.split('#')[0]
    }

    return url
  }

  /**
   * Wiki Sectionの表示管理
   * 言語とデータの存在チェックする
   * @returns
   */
  const isShowWikiSection = (): boolean => {
    return localePath === 'ja' && !!locationDetail.wiki
  }

  /**
   * ページトップにスクロールする
   */
  const moveScrollTop = () => {
    const pos = Math.max(
      window.pageYOffset,
      document.documentElement.scrollTop,
      document.body.scrollTop
    )
    if (pos > 0) {
      document.querySelector('body').scrollTo(0, 0)
    }
  }

  /**
   * ページ変更時の処理
   */
  const handlePageChange = useCallback(
    (page: number) => {
      if (page === currentPage) {
        return
      }

      const newQuery = { ...router.query }

      if (page === 1) {
        // ページ1の場合はpageパラメータを削除
        delete newQuery.page
      } else {
        // ページ2以降はpageパラメータを設定
        newQuery.page = page.toString()
      }

      // 繁体字の場合、zh_twをzh-TWに変換
      const locale = props.locale.replace('zh_tw', 'zh-TW')

      // クエリパラメータを更新
      router.push(
        {
          pathname: `/${locale}${router.pathname}`,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    },
    [currentPage, router]
  )

  const handleRecommendedFilterChange = useCallback(
    (condition: RecommendedFilterCondition) => {
      const nextFilter = recommendedFilter === condition ? null : condition

      if (currentPage !== 1) {
        isResettingRecommendedFilterPageRef.current = true
        handlePageChange(1)
      }

      switch (condition) {
        case 'today':
          sendGtmEvent({ eventName: GTM_EVENT_CLICK_FILTER_TODAY })
          break
        case 'tomorrow':
          sendGtmEvent({ eventName: GTM_EVENT_CLICK_FILTER_TOMORROW })
          break
        case 'open24Hours':
          sendGtmEvent({ eventName: GTM_EVENT_CLICK_FILTER_OPEN_24_HOURS })
          break
      }

      setRecommendedFilter(nextFilter)
    },
    [recommendedFilter, currentPage, handlePageChange]
  )

  //初期化処理
  useEffect(() => {
    openIntercomMessage()

    //API
    if (uriParams.isCity) {
      dispatch(fetchLocationDetailRequest(uriParams.citySlug, 'city', uriParams.countryCode))
    } else {
      dispatch(
        fetchLocationDetailRequest(
          uriParams.id,
          'location',
          uriParams.countryCode,
          uriParams.citySlug
        )
      )
    }

    // スクロールトップ
    moveScrollTop()
  }, [uriParams.isCity, uriParams.citySlug, uriParams.countryCode, uriParams.id])

  useEffect(() => {
    setIsLoading(false)
    setIsInitialLoad(false)
  }, [dispatch])

  // パスが変更時にスクロールトップ
  useEffect(() => {
    moveScrollTop()
    // パスが変更された場合は初回ロードフラグをリセット
    setIsInitialLoad(true)
  }, [asPath])

  //場所詳細が変更時
  useEffect(() => {
    if (!locationDetail) return

    initBreadcrumbMenus()

    const acceptLanguage = getAcceptLanguageByLocale(props.locale)

    //メインビジュアル
    setMainVisualSectionStyle({
      backgroundImage: !!locationDetail.image_url ? `url(${locationDetail.image_url})` : null,
    })

    if (isResettingRecommendedFilterPageRef.current && currentPage !== 1) {
      return
    }

    // URLのpageパラメータをリセット（ページ1に戻す）
    // ただし、初回ロード時（直接アクセス時）は除く
    if (router.query.page && !isInitialLoad) {
      const newQuery = { ...router.query }
      delete newQuery.page
      router.push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    }

    // 都道府県をセットする
    setThisPrefecture({
      id: uriParams.isCity ? locationDetail.id : locationDetail.region?.id,
      slug: uriParams.citySlug,
      name: locationDetail.name,
      lat: Number(locationDetail.latitude),
      lon: Number(locationDetail.longitude),
      en_name: locationDetail.en_name,
      en_area_name: '',
      zh_cn_name: locationDetail.zh_cn_name,
      zh_cn_area_name: '',
      zh_tw_name: locationDetail.zh_tw_name,
      zh_tw_area_name: '',
      zoom_pc: 0,
      zoom_sp: 0,
    })

    loadNearbySpaces(
      buildNearbySpacesParams({
        locationDetail,
        isCity: uriParams.isCity,
        page: currentPage,
        pageSize: ITEM_COUNT,
        locale: props.locale,
        filterCondition: recommendedFilter,
      })
    )

    dispatch(
      fetchLocationCoinLockersRequest(
        locationDetail.latitude,
        locationDetail.longitude,
        acceptLanguage
      )
    )

    fetchLegacyCoinLockers({
      latitude: locationDetail.latitude,
      longitude: locationDetail.longitude,
    })

    if (isResettingRecommendedFilterPageRef.current && currentPage === 1) {
      isResettingRecommendedFilterPageRef.current = false
    }

    getAreaLockers({ latitude: locationDetail.latitude, longitude: locationDetail.longitude })
  }, [
    locationDetail,
    currentPage,
    recommendedFilter,
    uriParams.isCity,
    props.locale,
    loadNearbySpaces,
    fetchLegacyCoinLockers,
  ])

  const displayRecommendedSpaces = useMemo(
    () => nearbySpaces.map(mapNearbySpaceToRecommendedSpace),
    [nearbySpaces]
  )

  const displayRecommendedSpacesLoading = isNearbySpacesLoading

  // 都道府県が変更されたら、施設データを取得する
  useEffect(() => {
    if (!thisPrefecture?.id) {
      return
    }

    fetchStations(thisPrefecture.id)
    fetchFacilities(thisPrefecture.id, { type: 'facility' })
  }, [thisPrefecture?.id, fetchStations, fetchFacilities])

  /**
   * コピーする
   * @returns
   */
  const handleShareClick = () => {
    if (!navigator.clipboard) {
      return
    }

    const url: string = createShareUrl()
    const text: string = getShareText()
    const shareText: string = `${text} ${url}`

    navigator.clipboard.writeText(shareText).then(() => {
      alert('copied')
    })
  }

  return (
    <Layout>
      <>
        <Meta
          title={metaTitle}
          description={metaDescription}
          url={`/${props.locale === 'default' ? 'ja' : props.locale}${asPath}`}
          image={props.shareImageUrl}
        />
        {isLoading ? (
          <LoadingComponent />
        ) : (
          <>
            <section
              className={`${styles.mainVisualSection} ${
                !mainVisualSectionStyle.backgroundImage && styles.mainVisualSectionNoneBg
              }`}
              style={mainVisualSectionStyle}
            >
              {!!mainVisualSectionStyle.backgroundImage && (
                <span className={styles.pseudoElement} />
              )}
              <p className={styles.mainVisualSectionShare}>
                <button
                  className={styles.mainVisualSectionShareButton}
                  onClick={handleShareClick}
                  type="button"
                >
                  <img
                    className={styles.mainVisualSectionShareButtonImage}
                    src="/share_icon_white.svg"
                    alt=""
                    width="27"
                    height="27"
                  />
                </button>
              </p>
              <h1 className={styles.mainVisualSectionTitle}>
                {t(
                  shouldUseWithAreaTranslation
                    ? 'AREAS.CITY.SLUG.MAIN_SPACE_TITLE_YEAR_LOCATION_WITH_AREA'
                    : 'AREAS.CITY.SLUG.MAIN_SPACE_TITLE_YEAR_LOCATION',
                  {
                    year: thisYear,
                    location: props.name,
                  }
                )}
              </h1>
            </section>
            <section
              className={`${styles.searchSection} ${
                !mainVisualSectionStyle.backgroundImage && styles.searchSectionNoneBg
              }`}
            >
              <div className={styles.sectionContainer}>
                <SearchForm
                  name={props.name}
                  latitude={locationDetail.latitude}
                  longitude={locationDetail.longitude}
                />
              </div>
            </section>

            <section className={styles.updatedAtSection}>
              <p className={styles.updatedAtText}>{getLastUpdateText()}</p>
            </section>

            <section className={styles.tabSection}>
              <div className={styles.tabMenus}>
                <button
                  className={`${styles.tabMenu} ${
                    spaceCoinLockerTabKey === SELECTED_TAB_KEY.space ? styles.tabMenuActive : ''
                  }`}
                  onClick={() => {
                    setSpaceCoinLockerTabKey(SELECTED_TAB_KEY.space)
                  }}
                >
                  <span className={styles.tabMenuIcon}>
                    {spaceCoinLockerTabKey === SELECTED_TAB_KEY.space ? (
                      <img
                        className={styles.tabMenuIconImage}
                        src="/suitcase_icon_blue.svg"
                        alt={t('META.PLACEHOLDER_LUGGAGE_ICON')}
                      />
                    ) : (
                      <img
                        className={styles.tabMenuIconImage}
                        src="/suitcase_icon_gray.svg"
                        alt={t('META.PLACEHOLDER_LUGGAGE_ICON')}
                      />
                    )}
                  </span>
                  <span className={styles.tabMenuTitle}>
                    {t('COMMON.LUGGAGE_STORAGE_PLACES')}（{recommendedSpacesTotalCount}）
                  </span>
                </button>
                <button
                  className={`${styles.tabMenu} ${
                    spaceCoinLockerTabKey === SELECTED_TAB_KEY.coinLocker
                      ? styles.tabMenuActive
                      : ''
                  }`}
                  onClick={() => {
                    setSpaceCoinLockerTabKey(SELECTED_TAB_KEY.coinLocker)
                  }}
                >
                  <span className={styles.tabMenuIcon}>
                    {spaceCoinLockerTabKey === SELECTED_TAB_KEY.coinLocker ? (
                      <img
                        className={styles.tabMenuIconImage}
                        src="/coin_locker_icon_blue.svg"
                        alt={t('META.PLACEHOLDER_LUGGAGE_ICON')}
                      />
                    ) : (
                      <img
                        className={styles.tabMenuIconImage}
                        src="/coin_locker_icon_gray.svg"
                        alt={t('META.PLACEHOLDER_LUGGAGE_ICON')}
                      />
                    )}
                  </span>
                  <span className={styles.tabMenuTitle}>
                    {t('COMMON.COIN_LOCKER.NAME')}（{lockers.length + legacyCoinLockers.length}）
                  </span>
                </button>
              </div>
              <div className={styles.tabContents}>
                <div
                  className={`${styles.tabContent} ${
                    spaceCoinLockerTabKey === SELECTED_TAB_KEY.space ? styles.tabContentActive : ''
                  }`}
                >
                  {/* 東京周辺のおすすめ荷物預け場所 */}
                  <section className={styles.recommendedSection}>
                    <div className={styles.sectionContainer}>
                      <SectionBottomBorderTitleComponent
                        text={t('AREAS.CITY.SLUG.RECOMMENDED_SELECT_TITLE', {
                          location: props.name,
                        })}
                        subTextElement={
                          <div className={styles.recommendedSectionSectionTitleSubElement}>
                            <img
                              className={styles.recommendedSectionSectionTitleSubElementImage}
                              src="/map_pin_icon_gray.svg"
                              alt=""
                            />
                            <span className={styles.recommendedSectionSectionTitleSubElementText}>
                              {t('COMMON.TOTAL_SPACE', {
                                total: recommendedSpacesTotalCount,
                              })}
                            </span>
                          </div>
                        }
                      />
                      <div className={styles.recommendedSectionFilters}>
                        {recommendedFilterOptions.map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => handleRecommendedFilterChange(option.key)}
                            className={`${styles.recommendedSectionFilterButton} ${
                              recommendedFilter === option.key
                                ? styles.recommendedSectionFilterButtonActive
                                : ''
                            }`}
                            aria-pressed={recommendedFilter === option.key}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <RecommendedSpaceCardListComponent
                        list={displayRecommendedSpaces}
                        totalCount={recommendedSpacesTotalCount}
                        currentPage={currentPage}
                        locale={props.locale}
                        itemsPerPage={ITEM_COUNT}
                        onPageChange={handlePageChange}
                        isLoading={displayRecommendedSpacesLoading}
                        usePagination={!displayRecommendedSpacesLoading}
                      />
                    </div>
                  </section>
                </div>
                <div
                  className={`${styles.tabContent} ${
                    spaceCoinLockerTabKey === SELECTED_TAB_KEY.coinLocker
                      ? styles.tabContentActive
                      : ''
                  }`}
                >
                  {/* 東京駅周辺のおすすめコインロッカー */}
                  <section id="coin-locker-section" className={`${styles.recommendedSection}`}>
                    <div className={styles.sectionContainer}>
                      <SectionBottomBorderTitleComponent
                        text={t('AREAS.CITY.SLUG.RECOMMENDED_COIN_LOCKER_SELECT_TITLE', {
                          location: props.name,
                          count: lockers.length + legacyCoinLockers.length,
                        })}
                        subTextElement={
                          <div className={styles.recommendedSectionSectionTitleSubElement}>
                            <img
                              className={styles.recommendedSectionSectionTitleSubElementImage}
                              src="/map_pin_icon_gray.svg"
                              alt=""
                            />
                            <span className={styles.recommendedSectionSectionTitleSubElementText}>
                              {t('COMMON.TOTAL_COIN_LOCKER', {
                                total: lockers.length + legacyCoinLockers.length,
                              })}
                            </span>
                          </div>
                        }
                      />
                      {(lockers?.length > 0 || legacyCoinLockers?.length > 0) && (
                        <CoinLockerMapComponent
                          centerLat={Number(locationDetail.latitude)}
                          centerLon={Number(locationDetail.longitude)}
                          coinLockers={lockers}
                          oldCoinLockers={legacyCoinLockers}
                          scrollToRecommendedCoinLockerSection={
                            scrollToRecommendedCoinLockerSection
                          }
                        />
                      )}
                      <RecommendedCoinLockerCardListComponent
                        lockers={lockers}
                        oldList={legacyCoinLockers}
                        locale={props.locale}
                      />
                    </div>
                  </section>
                </div>
              </div>
            </section>

            {/* 東京都の人気預かりエリア */}
            {displayRecommendedSpaces.length === 0 &&
              locationCoinLockers.length === 0 &&
              facilityRecommendationConfig && (
                <FacilityRecommendationsSection
                  latitude={locationDetail.latitude}
                  longitude={locationDetail.longitude}
                  type={facilityRecommendationConfig.type}
                  prefectureId={facilityRecommendationConfig.prefectureId}
                  locationName={props.name}
                  countrySlug={uriParams.countryCode}
                  citySlug={uriParams.citySlug}
                  localePath={localePath}
                  locale={props.locale}
                />
              )}

            {/* エクボクロークを使って荷物を預けよう */}
            <section className={styles.aboutServiceSection}>
              <div className={styles.sectionContainer}>
                <h2 className={styles.aboutServiceSectionTitle}>
                  <img
                    className={styles.aboutServiceSectionTitleImage}
                    src="/logo.webp"
                    alt="ecbo cloak"
                    width="138"
                  />
                  <strong className={styles.aboutServiceSectionTitleText}>
                    {t('AREAS.CITY.SLUG.USE_SERVICE_TITLE')}
                  </strong>
                </h2>
                <ul className={styles.aboutServiceSectionFeature}>
                  <li className={styles.aboutServiceSectionFeatureItem}>
                    <img
                      className={styles.aboutServiceSectionFeatureItemImage}
                      src="/feature_01_02.webp"
                      alt=""
                      width="135"
                    />
                    <p className={styles.aboutServiceSectionFeatureItemText}>
                      {t('AREAS.CITY.SLUG.FEATURE_MORE_PLACE_TITLE')}
                    </p>
                  </li>
                  <li className={styles.aboutServiceSectionFeatureItem}>
                    <img
                      className={styles.aboutServiceSectionFeatureItemImage}
                      src="/feature_02_02.webp"
                      alt=""
                      width="135"
                    />
                    <p className={styles.aboutServiceSectionFeatureItemText}>
                      {t('AREAS.CITY.SLUG.FEATURE_LEAVE_COIN_LOCKER_TITLE')}
                    </p>
                  </li>
                  <li className={styles.aboutServiceSectionFeatureItem}>
                    <img
                      className={styles.aboutServiceSectionFeatureItemImage}
                      src="/feature_03_02.webp"
                      alt=""
                      width="135"
                    />
                    <p className={styles.aboutServiceSectionFeatureItemText}>
                      {t('AREAS.CITY.SLUG.FEATURE_SIZE_TITLE')}
                    </p>
                  </li>
                </ul>

                {/* 使い方 */}
                <AccordionBlock title={t('AREAS.CITY.SLUG.CHECK_HOW_TO_USE')}>
                  <div className={styles.howToUseSection}>
                    <ul className={styles.howToUseList}>
                      <li className={styles.howToUseListItem}>
                        <dl className={styles.howToUseInfo}>
                          <dd className={styles.howToUseInfoVisual}>
                            <img
                              className={styles.howToUseInfoVisualImage}
                              src="/how_to_use_01.webp"
                              alt=""
                            />
                          </dd>
                          <dt className={styles.howToUseInfoTitle}>
                            {t('AREAS.CITY.SLUG.HOW_TO_USE_RESERVE')}
                          </dt>
                        </dl>
                      </li>
                      <li className={styles.howToUseListItem}>
                        <dl className={styles.howToUseInfo}>
                          <dd className={styles.howToUseInfoVisual}>
                            <img
                              className={styles.howToUseInfoVisualImage}
                              src="/how_to_use_02.webp"
                              alt=""
                            />
                          </dd>
                          <dt className={styles.howToUseInfoTitle}>
                            {t('AREAS.CITY.SLUG.HOW_TO_USE_CHECKIN')}
                          </dt>
                        </dl>
                      </li>
                      <li className={styles.howToUseListItem}>
                        <dl className={styles.howToUseInfo}>
                          <dd className={styles.howToUseInfoVisual}>
                            <img
                              className={styles.howToUseInfoVisualImage}
                              src="/how_to_use_03.webp"
                              alt=""
                            />
                          </dd>
                          <dt className={styles.howToUseInfoTitle}>
                            {t('AREAS.CITY.SLUG.HOW_TO_USE_DAY')}
                          </dt>
                        </dl>
                      </li>
                    </ul>
                  </div>
                </AccordionBlock>
                {/* 4つの特徴 */}
                <AccordionBlock title={t('AREAS.CITY.SLUG.CHECK_FEATURE_TITLE')}>
                  <div className={styles.featureSection}>
                    <ul className={styles.featureList}>
                      <li className={styles.featureListItem}>
                        <dl className={styles.featureInfo}>
                          <dd className={styles.featureInfoIcon}>
                            <img
                              className={styles.featureInfoIconImage}
                              src="/feature_01.webp"
                              alt=""
                            />
                          </dd>
                          <dt className={styles.featureInfoTitle}>
                            {t('AREAS.CITY.SLUG.FEATURE_PARTNERSHIP_TITLE')}
                          </dt>
                          <dd className={styles.featureInfoDescription}>
                            {t('AREAS.CITY.SLUG.FEATURE_PARTNERSHIP_DESCRIPTION')}
                          </dd>
                        </dl>
                      </li>
                      <li className={styles.featureListItem}>
                        <dl className={styles.featureInfo}>
                          <dd className={styles.featureInfoIcon}>
                            <img
                              className={styles.featureInfoIconImage}
                              src="/feature_02.webp"
                              alt=""
                            />
                          </dd>
                          <dt className={styles.featureInfoTitle}>
                            {t('AREAS.CITY.SLUG.FEATURE_SPACE_TITLE')}
                          </dt>
                          <dd className={styles.featureInfoDescription}>
                            {t('AREAS.CITY.SLUG.FEATURE_SPACE_DESCRIPTION')}
                          </dd>
                        </dl>
                      </li>
                      <li className={styles.featureListItem}>
                        <dl className={styles.featureInfo}>
                          <dd className={styles.featureInfoIcon}>
                            <img
                              className={styles.featureInfoIconImage}
                              src="/feature_03.webp"
                              alt=""
                            />
                          </dd>
                          <dt className={styles.featureInfoTitle}>
                            {t('AREAS.CITY.SLUG.FEATURE_SIZE_TITLE')}
                          </dt>
                          <dd className={styles.featureInfoDescription}>
                            {t('AREAS.CITY.SLUG.FEATURE_SIZE_DESCRIPTION')}
                          </dd>
                        </dl>
                      </li>
                      <li className={styles.featureListItem}>
                        <dl className={styles.featureInfo}>
                          <dd className={styles.featureInfoIcon}>
                            <img
                              className={styles.featureInfoIconImage}
                              src="/feature_04.webp"
                              alt=""
                            />
                          </dd>
                          <dt className={styles.featureInfoTitle}>
                            {t('AREAS.CITY.SLUG.FEATURE_GUARANTEE_TITLE')}
                          </dt>
                          <dd className={styles.featureInfoDescription}>
                            {t('AREAS.CITY.SLUG.FEATURE_GUARANTEE_DESCRIPTION')}
                          </dd>
                        </dl>
                      </li>
                    </ul>
                  </div>
                </AccordionBlock>
                {/* 料金プラン */}
                <AccordionBlock title={t('AREAS.CITY.SLUG.CHECK_PLAN_TITLE')}>
                  <div className={styles.planSection}>
                    <ul className={styles.planList}>
                      <li className={styles.planListItem}>
                        <PlanCard
                          title={t('AREAS.CITY.SLUG.PLAN_BAG_SIZE')}
                          countryCode={uriParams.countryCode}
                          iconImage={'/plan_size_bag.webp'}
                          sizeType={PRICE_SIZE_TYPE_BAG}
                          description={t('AREAS.CITY.SLUG.PLAN_BAG_DESCRIPTION')}
                        />
                      </li>
                      <li className={styles.planListItem}>
                        <PlanCard
                          title={t('AREAS.CITY.SLUG.PLAN_SUIT_SIZE')}
                          countryCode={uriParams.countryCode}
                          iconImage={'/plan_size_suitcase.webp'}
                          sizeType={PRICE_SIZE_TYPE_SUITCASE}
                          description={t('AREAS.CITY.SLUG.PLAN_SUIT_DESCRIPTION')}
                        />
                      </li>
                    </ul>
                  </div>
                </AccordionBlock>
              </div>
            </section>

            {/* よくある質問 */}
            {uriParams.countryCode === 'jpn' && !!props.name && (
              <section className={styles.qaSection}>
                <div className={styles.sectionContainer}>
                  <SectionBottomBorderTitleComponent text={t('AREAS.QA.TITLE')} />
                  <Qa
                    locationName={props.name}
                    id={props.locationDetail.id}
                    ignoreIds={EXCEPTION_LOCATION_IDS}
                    useWithAreaTranslations={shouldUseWithAreaTranslation}
                  />
                </div>
              </section>
            )}

            {/* 東京都の人気預かりエリア */}
            {(displayRecommendedSpaces.length !== 0 || locationCoinLockers.length !== 0) &&
              facilityRecommendationConfig && (
                <FacilityRecommendationsSection
                  latitude={locationDetail.latitude}
                  longitude={locationDetail.longitude}
                  type={facilityRecommendationConfig.type}
                  prefectureId={facilityRecommendationConfig.prefectureId}
                  locationName={props.name}
                  countrySlug={uriParams.countryCode}
                  citySlug={uriParams.citySlug}
                  localePath={localePath}
                  locale={props.locale}
                />
              )}

            {/* 東京都の荷物預かり情報 */}
            <section className={styles.pageDescriptionSection}>
              <div className={styles.sectionContainer}>
                <SectionBottomBorderTitleComponent
                  text={t(
                    shouldUseWithAreaTranslation
                      ? 'AREAS.CITY.SLUG.LOCATION_STORAGE_INFORMATION_WITH_AREA'
                      : 'AREAS.CITY.SLUG.LOCATION_STORAGE_INFORMATION',
                    {
                      location: props.name,
                    }
                  )}
                />
                {spaceCoinLockerTabKey === SELECTED_TAB_KEY.space ? (
                  <p className={styles.pageDescriptionSectionText}>
                    {t('AREAS.CITY.SLUG.MAIN_DESCRIPTION', {
                      location: props.name,
                    })}
                  </p>
                ) : (
                  <p className={styles.pageDescriptionSectionText}>
                    {t('AREAS.CITY.SLUG.MAIN_DESCRIPTION_COIN_LOCKER', {
                      location: props.name,
                    })}
                  </p>
                )}
              </div>
            </section>
            {/* 東京駅の観光情報について */}
            {isShowWikiSection() && !EXCEPTION_LOCATION_IDS.includes(props.locationDetail.id) && (
              <section className={styles.wikiSection}>
                <div className={styles.sectionContainer}>
                  <SectionBottomBorderTitleComponent
                    text={t(
                      shouldUseWithAreaTranslation
                        ? 'AREAS.CITY.SLUG.WIKI_TITLE_WITH_AREA'
                        : 'AREAS.CITY.SLUG.WIKI_TITLE',
                      {
                        location: props.name,
                      }
                    )}
                  />
                  <p className={styles.wikiSectionText}>{locationDetail.wiki}</p>
                </div>
              </section>
            )}

            {/* アプリダウンロード */}
            <section className={styles.storeSection}>
              <div className={styles.sectionContainer}>
                <CardStoreComponent />
              </div>
            </section>

            {/* 東京ドームのみ注釈を表示 */}
            {locationDetail?.id === 77 && (
              <div className={styles.noticeSection}>
                <div className={styles.title}>{t('AREAS.CITY.ANNOTATION')}</div>
              </div>
            )}
            {/* エリア一覧 */}
            {uriParams.countryCode === 'jpn' && (
              <section className={styles.locationSection}>
                <div className={styles.sectionContainer}>
                  <LocationSection
                    locations={[]}
                    prefectures={prefectures ?? []}
                    isSelectedArea={false}
                    onSelectArea={undefined}
                    setIsSelectedArea={() => {}}
                    stations={[]}
                    facilities={[]}
                    locale={props.locale}
                    countryCode={uriParams.countryCode}
                    isShowBackButton={false}
                    isShowAreaName={false}
                    sectionTitle={t('AREAS.CITY.SLUG.LOCATION_SECTION_TITLE_AREA')}
                    areaPlaceListHeadingStyle="h3"
                    placeName={getLocationData().location_name}
                  />
                </div>
              </section>
            )}

            {/* 駅・施設一覧 - 台湾 */}
            {uriParams.countryCode === 'twn' && (
              <section className={styles.locationSection}>
                <div className={styles.sectionContainer}>
                  <LocationSection
                    locations={[]}
                    prefectures={prefectures ?? []}
                    isSelectedArea={false}
                    onSelectArea={undefined}
                    setIsSelectedArea={() => {}}
                    stations={[]}
                    facilities={[]}
                    locale={props.locale}
                    countryCode={uriParams.countryCode}
                    isShowBackButton={false}
                    isShowAreaName={false}
                    sectionTitle={t('AREAS.CITY.SLUG.LOCATION_SECTION_TITLE_AREA')}
                    areaPlaceListHeadingStyle="h3"
                    placeName={getLocationData().location_name}
                  />
                </div>
              </section>
            )}

            {/* 駅・施設一覧 - 日本 */}
            {uriParams.countryCode === 'jpn' && thisPrefecture && (
              <section className={styles.locationSection}>
                <div className={styles.sectionContainer}>
                  <LocationSection
                    locations={[]}
                    isSelectedArea={true}
                    onSelectArea={() => {}}
                    setIsSelectedArea={() => {}}
                    selectedPrefecture={thisPrefecture}
                    stations={stations}
                    facilities={groupedFacilities?.facilities ?? []}
                    locale={props.locale}
                    countryCode={uriParams.countryCode}
                    isShowBackButton={false}
                    isShowAreaName={false}
                    sectionTitle={t('AREAS.CITY.SLUG.LOCATION_SECTION_TITLE_PLACE_PREFECTURE', {
                      place: getLocationData().location_name,
                    })}
                    areaPlaceListHeadingStyle="h3"
                    placeName={getLocationData().location_name}
                  />
                </div>
              </section>
            )}

            {/* オーナーとして登録 */}
            <div className={styles.sectionContainer}>
              <OwnerSection />
            </div>

            <BreadcrumbLayout menus={breadcrumbMenus} />
          </>
        )}
      </>
    </Layout>
  )
}

export async function getServerSideProps({ locale, query }) {
  // 国コード
  const countryCode: string = query.country

  // 国コードチェック
  if (!COUNTRY_CODES.includes(countryCode)) {
    // 有効な国コードではない
    // トップにリダイレクト
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
    }
  }

  const translations = await serverSideTranslations(locale, ['common'])

  if (query.slug.length === 1) {
    const idOrSlug = query.slug[0]
    const prefectureResult = await fetchPrefectureByIdOrSlug(idOrSlug, locale)

    if (!prefectureResult.isOk || !prefectureResult.data) {
      return { notFound: true }
    }

    const prefecture = prefectureResult.data
    if (prefecture.latitude == null || prefecture.longitude == null) {
      return { notFound: true }
    }

    return {
      props: {
        locale: locale.replace('-', '_').toLowerCase(),
        name: prefecture.name,
        shareImageUrl: DEFAULT_SHARE_IMAGE_URL,
        locationDetail: mapPrefectureItemToLocationDetail(prefecture),
        ...translations,
      },
    }
  }

  if (query.slug.length === 2) {
    const facilityId = Number(query.slug[1])
    if (!Number.isInteger(facilityId) || facilityId <= 0) {
      return { notFound: true }
    }

    const urlPrefectureSlug = query.slug[0]
    const facilityResult = await fetchFacilityById(facilityId, locale)

    if (!facilityResult.isOk || !facilityResult.data) {
      return { notFound: true }
    }

    const facility = facilityResult.data
    if (!facility.prefecture) {
      return { notFound: true }
    }

    // API が prefecture.slug を返さない施設は URL スラッグとの一致検証をスキップする
    // （レガシー API と同様に facility id のみで解決する）
    if (facility.prefecture.slug && facility.prefecture.slug !== urlPrefectureSlug) {
      return { notFound: true }
    }

    return {
      props: {
        locale: locale.replace('-', '_').toLowerCase(),
        name: facility.name,
        shareImageUrl: facility.image_url ?? DEFAULT_SHARE_IMAGE_URL,
        locationDetail: mapFacilityToLocationDetail(facility),
        ...translations,
      },
    }
  }

  return { notFound: true }
}

export default CitySlugPage
